/**
 * ScheduleEditor.jsx - the Service Quote's calendar (Quote Master's Schedule
 * tab, as a web form). Three levels, each beating the one above it: the hour
 * boxes set every week, a week changed in the week table beats the boxes, and
 * a day set on the calendar beats both.
 *
 * On the calendar: click a day to step it work -> travel -> off; type hours in
 * a day (0 turns it off); or arm Work / Travel / Off and drag across days.
 * Colours come from the server's quote engine (the calc result's days): green
 * normal time, yellow the day has overtime, orange double time, blue travel,
 * grey off, red outline a company holiday (holidays start off).
 *
 * onChange takes an updater (prev => next), so a fast paint drag never loses
 * a day to a stale copy.
 *
 * The week is the rate card's (week, from quoteDraft weekOfSettings): the hour
 * boxes and the week table are named after its days (Mon-Fri / Saturday /
 * Sunday on the main card; Sat-Wed / Thursday / Friday on GLOBAL KSA's, whose
 * calendar also starts its rows on Saturday), and a week row is the week
 * starting on the card's first day.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MAX_ENGINEERS,
  cycleDay,
  editCount,
  monthGrid,
  monthsBetween,
  resetSchedule,
  setDays,
  setWeekOverride,
  typeHours,
  weekOfSettings,
} from './quoteDraft';
import { Btn, Field, GREEN, Grid, LINE, MUTED, NumInput, SelectInput, TextInput, fmtNum } from './quoteUi';

export const BUCKET_COLORS = {
  nt: '#CEEED0',
  ot: '#FFF0B3',
  dt: '#FDCDA0',
  travel: '#B0D6F5',
  off: '#F0F0F0',
};
const LEGEND = [
  ['nt', 'Normal time'],
  ['ot', 'Has overtime'],
  ['dt', 'Double time'],
  ['travel', 'Travel'],
  ['off', 'Off'],
];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** The colour a worked-out day shows (the engine's dayBucket). */
export function bucketOf(c) {
  if (!c) return 'off';
  if (c.k === 'T') return 'travel';
  if (c.k !== 'W') return 'off';
  if (c.dt > 0) return 'dt';
  if (c.ot > 0) return 'ot';
  return c.nt > 0 ? 'nt' : 'off';
}
const shortDate = (iso) => {
  const [, m, d] = iso.split('-');
  return `${MONTHS[+m - 1].slice(0, 3)} ${+d}`;
};

/** A day's hours box: typing is kept locally and set on Enter / leaving the box. */
function DayHours({ date, cell, onType }) {
  const shown = cell && cell.k === 'W' ? String(cell.h) : '';
  const [v, setV] = useState(shown);
  useEffect(() => {
    setV(shown);
  }, [shown]);
  const commit = () => {
    if (v !== shown) onType(date, v);
  };
  return (
    <input
      aria-label={`Hours on ${date}`}
      value={v}
      placeholder={cell && cell.k === 'T' ? 'travel' : ''}
      inputMode="decimal"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setV(e.target.value.replace(/[^0-9.]/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
        if (e.key === 'Escape') {
          setV(shown);
        }
      }}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        border: 'none',
        background: 'transparent',
        textAlign: 'center',
        fontSize: 12,
        fontWeight: 800,
        fontFamily: 'Montserrat',
        color: '#1F2328',
        padding: 0,
        outline: 'none',
      }}
    />
  );
}

export default function ScheduleEditor({ schedule, onChange, calcSchedule, roles, week }) {
  const s = schedule || {};
  const W = week || weekOfSettings(null);
  const L = W.labels;
  const dowHead = Array.from({ length: 7 }, (_, i) => DOW[(W.gridStart + i) % 7]);
  const [slot, setSlot] = useState(0); // 0 = one calendar for everyone
  const [paint, setPaint] = useState(''); // '' | 'W' | 'T' | 'X'
  const painting = useRef(false);
  const painted = useRef(new Set());
  useEffect(() => {
    const up = () => {
      painting.current = false;
      painted.current = new Set();
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);
  const n = Number(s.engineers) || 1;
  useEffect(() => {
    if (slot > n) setSlot(0);
  }, [n, slot]);

  const days = useMemo(() => {
    const m = new Map();
    for (const d of (calcSchedule && calcSchedule.days) || []) m.set(d.date, d);
    return m;
  }, [calcSchedule]);
  const cellOf = (date) => {
    const d = days.get(date);
    return d ? d.slots[Math.max(0, slot - 1)] : null;
  };
  const weeks = (calcSchedule && calcSchedule.weeks) || [];
  const months = monthsBetween(s.start, s.end);
  const inRange = (date) => !!date && s.start && s.end && date >= s.start && date <= s.end;
  const set = (k) => (v) => onChange((prev) => ({ ...prev, [k]: v }));

  const paintAt = (date) => {
    if (!paint || painted.current.has(date)) return;
    painted.current.add(date);
    onChange((prev) => setDays(prev, [date], { k: paint }, slot));
  };
  const onDown = (date) => {
    if (paint) {
      painting.current = true;
      painted.current = new Set();
      paintAt(date);
      return;
    }
    const c = cellOf(date);
    onChange((prev) => cycleDay(prev, date, c ? c.k : 'X', slot));
  };
  const onEnter = (date, e) => {
    if (painting.current && e.buttons === 1) paintAt(date);
  };
  const onType = (date, raw) => onChange((prev) => typeHours(prev, date, raw, slot));

  const edits = editCount(s);
  const t = (calcSchedule && calcSchedule.slots) || [];
  const sum = (k) => t.reduce((a, x) => a + (x[k] || 0), 0);
  const travel = sum('travelDays');

  return (
    <div>
      <Grid cols={6}>
        <Field label="Start date">
          <TextInput type="date" value={s.start} onChange={set('start')} />
        </Field>
        <Field label="End date">
          <TextInput type="date" value={s.end} onChange={set('end')} />
        </Field>
        <Field label="Engineers on site">
          <SelectInput
            value={String(n)}
            onChange={(v) => set('engineers')(Number(v))}
            options={Array.from({ length: MAX_ENGINEERS }, (_, i) => String(i + 1))}
          />
        </Field>
        <Field label={`${L.wd} hrs / day`} title="0 means nobody works weekdays">
          <NumInput value={s.wd} onChange={set('wd')} />
        </Field>
        {L.sat ? (
          <Field label={`${L.sat} hrs`} title={`0 means ${L.sat} is off`}>
            <NumInput value={s.sat} onChange={set('sat')} />
          </Field>
        ) : (
          <span />
        )}
        {L.sun ? (
          <Field label={`${L.sun} hrs`} title={`0 means ${L.sun} is off. Always double time.`}>
            <NumInput value={s.sun} onChange={set('sun')} />
          </Field>
        ) : (
          <span />
        )}
      </Grid>

      {calcSchedule && calcSchedule.problem && (
        <div
          role="alert"
          style={{
            marginTop: 10,
            padding: '6px 10px',
            background: '#FFEBEE',
            border: '1px solid #FFCDD2',
            borderRadius: 6,
            color: '#B71C1C',
            fontSize: 11.5,
            fontWeight: 600,
          }}
        >
          {calcSchedule.problem}
        </div>
      )}

      {weeks.length > 0 && (
        <>
          <div style={{ marginTop: 12, fontSize: 11.5, color: '#1F2328', fontWeight: 600 }}>
            {n} {n === 1 ? 'engineer' : 'engineers'} · {fmtNum(sum('daysWorked'))} working days
            {travel > 0 ? ` · ${fmtNum(travel)} travel days (${fmtNum(sum('trips'))} round trips)` : ''}
            {' · '}
            {fmtNum(sum('totalHours'))} hours ({fmtNum(sum('nt'))} NT, {fmtNum(sum('ot'))} OT, {fmtNum(sum('dt'))} DT)
          </div>

          <details style={{ marginTop: 10 }} open={weeks.length <= 6}>
            <summary style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, color: GREEN }}>
              Hours week by week - change a week here and it beats the boxes above
            </summary>
            <table style={{ borderCollapse: 'collapse', marginTop: 6, fontSize: 11.5 }}>
              <thead>
                <tr>
                  {['Wk', 'Week of', L.wd, L.sat.slice(0, 3), L.sun.slice(0, 3), ''].map((h, i) => (
                    <th key={i} style={{ textAlign: 'left', fontSize: 10, color: MUTED, padding: '2px 8px', fontWeight: 700 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeks.map((w) => (
                  <tr key={w.monday}>
                    <td style={{ padding: '2px 8px', color: MUTED }}>{w.week}</td>
                    <td style={{ padding: '2px 8px', fontWeight: 600 }}>{shortDate(w.monday)}</td>
                    {['wd', 'sat', 'sun'].map((k) => {
                      if ((k === 'sat' && !L.sat) || (k === 'sun' && !L.sun)) return <td key={k} />;
                      const over = s.weekOverrides && s.weekOverrides[w.monday] && s.weekOverrides[w.monday][k] !== undefined;
                      return (
                        <td key={k} style={{ padding: '2px 4px', width: 70 }}>
                          <NumInput
                            aria-label={`Week of ${w.monday} ${k}`}
                            value={over ? String(s.weekOverrides[w.monday][k]) : ''}
                            placeholder={String(w[k])}
                            onChange={(v) => onChange((prev) => setWeekOverride(prev, w.monday, k, v))}
                            style={over ? { background: '#FFF8E1', borderColor: '#F2C94C', fontWeight: 700 } : undefined}
                          />
                        </td>
                      );
                    })}
                    <td style={{ padding: '2px 8px', fontSize: 10, color: MUTED }}>{w.changed.length ? 'changed' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </>
      )}

      {months.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>Paint</span>
            {[
              ['W', 'Work'],
              ['T', 'Travel'],
              ['X', 'Off'],
            ].map(([k, label]) => (
              <Btn
                key={k}
                small
                primary={paint === k}
                onClick={() => setPaint((p) => (p === k ? '' : k))}
                title={`Then drag across days to make them ${label.toLowerCase()}. Press again to stop.`}
              >
                {label}
                {paint === k ? ' ON' : ''}
              </Btn>
            ))}
            <Btn
              small
              disabled={!edits}
              title="Forget every day set by hand and every changed week"
              onClick={() => {
                if (window.confirm('Refill every week from the boxes and forget every day you set by hand?')) onChange(resetSchedule);
              }}
            >
              Reset{edits ? ` (${edits})` : ''}
            </Btn>
            {n > 1 && (
              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>Calendar for</span>
                <SelectInput
                  aria-label="Calendar for"
                  value={String(slot)}
                  onChange={(v) => setSlot(Number(v))}
                  style={{ width: 200 }}
                  options={[
                    { value: '0', label: 'Everyone' },
                    ...Array.from({ length: n }, (_, i) => ({
                      value: String(i + 1),
                      label: `${i + 1}  ${(roles && roles[i]) || `Engineer ${i + 1}`}`,
                    })),
                  ]}
                />
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 10.5, color: MUTED, marginBottom: 8 }}>
            {LEGEND.map(([k, label]) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: BUCKET_COLORS[k], border: '1px solid #D7DCD9' }} />
                {label}
              </span>
            ))}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, border: '2px solid #C00000' }} />
              Company holiday
            </span>
            <span>Click a day: work, travel, off. Type hours in a day. 0 turns it off.</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14, userSelect: 'none' }}>
            {months.map(({ year, month }) => (
              <div key={`${year}-${month}`} data-testid={`month-${year}-${month + 1}`}>
                <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 800, color: '#106828', marginBottom: 4 }}>
                  {MONTHS[month]} {year}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                  {dowHead.map((d) => (
                    <div key={d} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: '#6E6E6E', background: '#EEF2F0' }}>
                      {d}
                    </div>
                  ))}
                  {monthGrid(year, month, W.gridStart)
                    .flat()
                    .map((date, i) => {
                      if (!date) return <div key={`e${i}`} />;
                      const live = inRange(date);
                      const d = days.get(date);
                      const c = live ? cellOf(date) : null;
                      const bucket = live ? bucketOf(c) : null;
                      const holiday = d && d.holiday;
                      return (
                        <div
                          key={date}
                          data-date={date}
                          data-bucket={bucket || 'none'}
                          role={live ? 'button' : undefined}
                          aria-label={
                            live
                              ? `${date}: ${c ? { W: `work ${c.h} h`, T: 'travel', X: 'off' }[c.k] : 'off'}${holiday ? ', holiday' : ''}`
                              : undefined
                          }
                          onMouseDown={
                            live
                              ? (e) => {
                                  e.preventDefault();
                                  onDown(date);
                                }
                              : undefined
                          }
                          onMouseEnter={live ? (e) => onEnter(date, e) : undefined}
                          style={{
                            minHeight: 34,
                            borderRadius: 3,
                            cursor: live ? (paint ? 'crosshair' : 'pointer') : 'default',
                            background: live ? BUCKET_COLORS[bucket] : '#FBFBFB',
                            border: holiday ? '2px solid #C00000' : `1px solid ${live ? '#D7DCD9' : '#F2F2F2'}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            padding: '1px 2px',
                            opacity: live ? 1 : 0.5,
                          }}
                        >
                          <span style={{ fontSize: 8.5, color: '#555', textAlign: 'left' }}>{+date.slice(8)}</span>
                          {live && <DayHours date={date} cell={c} onType={onType} />}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
          {n > 1 &&
            slot === 0 &&
            calcSchedule &&
            calcSchedule.days.some((d) => d.slots.some((c) => c.k !== d.slots[0].k || c.h !== d.slots[0].h)) && (
              <div style={{ marginTop: 8, fontSize: 10.5, fontStyle: 'italic', color: '#B40000' }}>
                The engineers have different days. This calendar shows engineer 1, and changing a day here sets every engineer to match it.
              </div>
            )}
        </div>
      )}
      {!months.length && !(calcSchedule && calcSchedule.problem) && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: MUTED, border: `1px dashed ${LINE}`, borderRadius: 8, padding: 12 }}>
          Set the start and end dates (travel days included) and the calendar lays itself out. A quote with no dates can still price Other
          lines.
        </div>
      )}
    </div>
  );
}
